import { get } from "svelte/store";

import API from "../API/API";
import AuthConfig from "./AuthConfig";
import AWSCognitoIdentityProvider from "./AWSCognitoIdentityProvider";
import AWSOAuthFlowHelper from "./AWSOAuthFlowHelper";
import AuthFlowType from "./Enums/AuthFlowType";
import AuthState from "./Enums/AuthState";
import AuthStore from "./Store";

import Analytics from "../Analytics/Analytics";
import LocalStorageHelper from "../Helpers/LocalStorageHelper";

import Feature from "../Branding/Feature";
import IdentityHelper from "../Branding/IdentityHelper";
import Company from "../Models/Company";
import ROI from "../Models/ROI";
import User from "../Models/User";

import IdentityData from "../Branding/IdentityData";
import { identity } from "../Branding/Store";

const AuthActions = {};

AuthActions.init = () => {
	AuthStore.token.set(AuthStore.helpers._generateEmptyToken());
	AuthStore.userInfo.set(AuthStore.helpers._generateEmptyUserInfo());

	AuthStore.actions.load();
	AuthStore.meta.state.subscribe((value) => {
		identity.set(IdentityHelper.current());
	});

	if (AuthConfig.authFlowType === AuthFlowType.OAUTH) {
		AWSOAuthFlowHelper.start();
	}
};

// We need to clear session data if the version has been updated.
// We don't want old data structures sticking around, or to be accessing data structures that don't yet exist.
AuthActions.load = () => {
	if (LocalStorageHelper.has("auth")) {
		let data = JSON.parse(LocalStorageHelper.get("auth"));
		if (data.meta.version !== __VERSION) {
			LocalStorageHelper.clearAll();
			AuthStore.actions.reset();
			data = AuthStore.helpers._generateSaveData();
		}
		AuthStore.token.set(data.token);
		AuthStore.userInfo.set(data.userInfo);

		// We are logged in, use session rois until logged in
		AuthStore.session.rois.set(
			data.session.rois.map((item) => {
				// these ROIs don't currently contain the geojson!
				return new ROI(item);
			})
		);
		AuthStore.session.alertTypes.set(data.session.alertTypes);

		const company = new Company(data.session.company);
		const user = new User(data.session.user);
		user.company = company;
		AuthStore.session.company.set(company);
		AuthStore.session.user.set(user);

		AuthStore.forms.login.username.set(get(AuthStore.userInfo).username);
		if (AuthStore.helpers.isLoggedIn()) {
			// TODO: HACK. REMOVE WHEN FIXING CLIENT SWITCHER.
			if (data.userInfo.forceIdentity !== "") {
				// If we're an admin checking other clients then don't update user info.
				AuthStore.meta.state.set(AuthState.LOGGED_IN);
				return;
			}
			// We don't set LOGGED_IN straight away because we want the user info to refresh.
			// This will update the local cache ROIs, alert types and user permissions.
			// Instead we set LOGGING_IN...
			AuthStore.meta.state.set(AuthState.LOGGING_IN);
			AuthActions.updateCurrentUser();
		}
	}
};
AuthActions.save = () => {
	const data = AuthStore.helpers._generateSaveData();
	LocalStorageHelper.set("auth", JSON.stringify(data));
};

AuthActions.reset = () => {
	AuthStore.token.set(AuthStore.helpers._generateEmptyToken());
	AuthStore.userInfo.set(AuthStore.helpers._generateEmptyUserInfo());
};
AuthActions.login = (username, password) => {
	AuthStore.meta.state.set(AuthState.LOGGING_IN);
	AuthStore.forms.login.error.set("");
	AuthStore.forms.login.submitting.set(true);

	Analytics.event("login_attempt", { category: "auth", username: username });

	AWSCognitoIdentityProvider.login(username, password)
		.then((response) => {
			const data = response.data;
			const challengeName = data.ChallengeName;
			const session = data.Session;
			if (challengeName) {
				AuthStore.actions._handleChallenge(challengeName, session);
				AuthStore.forms.login.submitting.set(false);
			}
			else if (data.AuthenticationResult) {
				AuthStore.actions._handleAuthenticationResult(data.AuthenticationResult, "login");
			}
			else {
				console.warn("Unknown code pathway (login)", response);
				AuthStore.forms.login.submitting.set(false);
			}
		})
		.catch((error) => {
			Analytics.event("login_failed", { category: "auth", username: username });

			// Note: Setting 'submitting' to false in multiple places because it shouldn't be set until _hanndleAuthenticationResult is complete
			AuthStore.forms.login.submitting.set(false);
			AuthStore.forms.login.error.set(error.message);
			AuthStore.meta.state.set(AuthState.LOGGED_OUT);
		})
		.finally(() => {
			AuthStore.actions.save();
		});
};

AuthActions.setNewPassword = (username, password) => {
	AuthStore.forms.login.submitting.set(true);
	const session = get(AuthStore.meta.challenge.session);
	AWSCognitoIdentityProvider.setNewPassword(username, password, session)
		.then((response) => {
			const data = response.data;
			const challengeName = data.ChallengeName;
			const session = data.Session;
			if (challengeName) {
				AuthStore.actions._handleChallenge(challengeName, session);
				AuthStore.forms.login.submitting.set(false);
			}
			else if (data.AuthenticationResult) {
				AuthStore.actions._handleAuthenticationResult(data.AuthenticationResult, "new_password");
			}
			else {
				console.error("Unknown code pathway (setNewPassword)", response);
				AuthStore.forms.login.submitting.set(false);
			}
		})
		.catch((error) => {
			AuthStore.forms.login.submitting.set(false);
			AuthStore.forms.login.error.set(error.message);
			AuthStore.meta.state.set(AuthState.LOGGED_OUT);
		});
};

AuthActions.refresh = () => {
	const refreshToken = get(AuthStore.token).refresh_token;
	AWSCognitoIdentityProvider.refresh(refreshToken)
		.then((response) => {
			const data = response.data;
			if (data.__type !== undefined) {
				console.error(data.__type, data.message);
			}
			else if (data.AuthenticationResult) {
				AuthStore.actions._handleAuthenticationResult(data.AuthenticationResult, "refresh_token");
			}
		})
		.catch((err) => {
			console.error(err);
			AuthStore.actions.logout(false);
		});
};

AuthActions.forgotPassword = (username) => {
	return AWSCognitoIdentityProvider.request("ForgotPassword", {
		Username: username
	});
};
AuthActions.forgotPasswordConfirm = (username, code, newPassword) => {
	return AWSCognitoIdentityProvider.request("ConfirmForgotPassword", {
		Username: username,
		ConfirmationCode: code,
		Password: newPassword
	});
};

AuthActions.logout = (redirect = false) => {
	console.error("logout");
	AuthStore.events.onStartLogout.dispatch();
	AuthStore.meta.state.set(AuthState.LOGGING_OUT);

	Analytics.event("logout", { category: "auth", username: get(AuthStore.userInfo).username });

	const accessToken = get(AuthStore.token).access_token;
	AWSCognitoIdentityProvider.logout(accessToken).finally(() => {
		AuthStore.token.set(AuthStore.helpers._generateEmptyToken());
		AuthStore.userInfo.set({
			...AuthStore.helpers._generateEmptyUserInfo(),
			username: get(AuthStore.userInfo).username
		});
		AuthStore.actions.save();

		// TODO: Consider refactoring this old code.
		identity.set(IdentityData.geollect);

		// if (redirect) {
		// 	window.location.href = window.location.href; // eslint-disable-line
		// }
		// else {
		AuthStore.meta.state.set(AuthState.LOGGED_OUT);
		AuthStore.events.onFinishLogout.dispatch();
		window.location.href = window.location.href; // eslint-disable-line
		// }
	});
};

AuthActions._handleChallenge = (challengeName, session) => {
	AuthStore.meta.state.set(AuthState.LOGGING_IN_CHALLENGE);
	AuthStore.meta.challenge.type.set(challengeName);
	AuthStore.meta.challenge.session.set(session);
};

AuthActions._handleAuthenticationResult = (authenticationResult, from = "login") => {
	const token = AuthStore.helpers._generateTokenFromAWSAuthenticationResult(authenticationResult);

	// We're here if we logged in successfully, changed our password or refreshed our token (once per hour).
	AuthStore.token.set(token);
	AuthStore.actions.save();

	if (from === "login") {
		Analytics.event("login_success", { category: "auth", username: get(AuthStore.forms.login.username) });
	}

	AuthActions.updateCurrentUser();
};

AuthActions.updateCurrentUser = () => {
	return new Promise((resolve, reject) => {
		API.currentUser()
			.then((response) => {
				const companyName = response.company.name.toLowerCase();
				const username = response.user.username;
				const features = Feature.processKeyedFeaturesIntoArray(response.user.features);
				// TODO: themes on login.
				let id = "";
				const companyNames = [
					"geollect",
					"geollectadmin",
					"west",
					"london",
					"border force",
					"national grid",
					"tdi_brooks",
					"one_illuminate",

				];
				if (companyNames.includes(companyName) || companyName.indexOf("standard") !== -1) {
					id = companyName;
				}
				// email not defined
				AuthStore.userInfo.set({ username: username, email: "", features: features, identity: id });

				const company = new Company(response.company);
				const user = new User(response.user);
				user.company = company;

				AuthStore.session.alertTypes.set(response.alert_types);
				AuthStore.session.rois.set(
					response.rois.map((item) => {
						// these ROIs don't currently contain the geojson!
						return new ROI(item);
					})
				);
				AuthStore.session.company.set(company);
				AuthStore.session.user.set(user);
				AuthStore.meta.state.set(AuthState.LOGGED_IN);
			})
			.catch((error) => {
				console.error(error);
				AuthStore.forms.login.error.set(error.data.detail);
				AuthStore.actions.logout(false);
				reject(error);
			})
			.finally(() => {
				AuthStore.forms.login.submitting.set(false);
				AuthStore.actions.save();

				resolve();
			});
	});
};

AuthActions.pingSession = () => {
	const timeIn5mins = Math.floor(Date.now() / 1000) + 300;
	const validUntil = get(AuthStore.token).valid_until;
	const loginWillExpire = timeIn5mins >= validUntil; // 5 minutes before
	const loginValid = validUntil >= Date.now() / 1000;
	if (loginWillExpire && get(AuthStore.meta.state) === AuthState.LOGGED_IN) {
		AuthStore.actions.refresh();
	}
	else if (!loginValid && get(AuthStore.meta.state) === AuthState.LOGGED_IN) {
		AuthStore.actions.logout(false);
	}
	return loginValid;
};

export default AuthActions;
